import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Wrench } from 'lucide-react';

const EquipmentFinancing = () => {
  return (
    <div className="bg-[#FAFAFA]">
      {/* Hero with image */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/1579356/pexels-photo-1579356.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-amber-950/90 via-slate-900/85 to-slate-900/75" />
        </div>
        <div className="absolute inset-0 noise-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 w-full py-28">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 mb-8">
              <Wrench className="h-4 w-4 text-amber-300" />
              <span className="text-xs font-semibold text-amber-200 uppercase tracking-wider">Equipment Financing</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Equipment<br /><span className="text-amber-300">Financing</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-xl">
              Fund the machinery, vehicles, and technology your business needs. The equipment itself serves as collateral, making approval faster.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/apply" data-testid="equipment-apply" className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-slate-100 transition-all">
                Apply Now <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-white/10 transition-all">Talk to Us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'var(--font-heading)' }}>Program Details</h2>
              {[
                { label: 'Financing Amount', value: 'Up to 100% of equipment cost' },
                { label: 'Term Length', value: 'Matched to equipment lifespan' },
                { label: 'Collateral', value: 'Equipment serves as collateral' },
                { label: 'Minimum Credit Score', value: '600+' },
                { label: 'Down Payment', value: 'As low as 0% for qualified borrowers' },
                { label: 'Approval Speed', value: 'As fast as 24 to 48 hours' },
              ].map((item) => (
                <div key={item.label} className="flex justify-between items-center py-4 border-b border-slate-200">
                  <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{item.label}</span>
                  <span className="text-sm font-bold text-slate-900">{item.value}</span>
                </div>
              ))}
            </div>
            <div className="space-y-8">
              {/* Image card */}
              <div className="relative rounded-2xl overflow-hidden h-52">
                <img src="https://images.pexels.com/photos/3998410/pexels-photo-3998410.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Equipment" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-amber-950/80 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>Built-In Collateral</div>
                  <p className="text-xs text-amber-100/80 mt-1">Equipment secures the loan, often meaning lower rates and easier approval.</p>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>What You Can Finance</h3>
                {['Heavy machinery & construction equipment', 'Commercial vehicles & fleets', 'Manufacturing equipment', 'Technology & IT infrastructure', 'Medical & dental equipment', 'Restaurant & kitchen equipment'].map((use) => (
                  <div key={use} className="flex items-center gap-3 mb-4 last:mb-0">
                    <CheckCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <span className="text-sm text-slate-600">{use}</span>
                  </div>
                ))}
              </div>

              <Link to="/apply" className="block bg-amber-600 text-white rounded-2xl p-8 group hover:bg-amber-500 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Finance Your Equipment</h3>
                    <p className="text-sm text-amber-200">Get started in minutes</p>
                  </div>
                  <ArrowRight className="h-6 w-6 text-white group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA with image */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1761504537447-e39a3085d697?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBlcXVpcG1lbnQlMjBoZWF2eSUyMG1hY2hpbmVyeXxlbnwwfHx8fDE3NzQ1MjQ4MTV8MA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/85" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Get the Equipment<br />Your Business Needs</h2>
          <p className="text-lg text-slate-400 mb-10 max-w-xl mx-auto">Don't let equipment costs hold you back. Finance what you need and start generating returns immediately.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/apply" className="inline-flex items-center gap-2 bg-amber-600 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-amber-500 transition-colors">Apply Now <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/10 transition-colors">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EquipmentFinancing;
